import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@craft/shared'
import { logout } from '../lib/firebase'
import Logo from '../components/Logo'
import AuthModal from '../components/AuthModal'

const FEATURES = [
  {
    icon: '⚛️',
    title: '組み立てて覚える',
    body: '原子をドラッグして結合でつなぐだけ。手を動かすから、丸暗記より構造が頭に残ります。',
  },
  {
    icon: '✅',
    title: 'その場で採点',
    body: '分子式（原子の数）と構造（つながり方）を自動で照合。何が足りないか一目でわかります。',
  },
  {
    icon: '📘',
    title: '共通テスト対応',
    body: 'メタン・エタノール・酢酸・ベンゼンなど、頻出分子と構造異性体を収録。',
  },
]

const SAMPLE = ['水 H₂O', 'メタン CH₄', 'エチレン C₂H₄', 'エタノール C₂H₆O', '酢酸 CH₃COOH', 'ベンゼン C₆H₆']

export default function LandingPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [showAuth, setShowAuth] = useState(false)

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #0a0e1a 0%, #111827 100%)', fontFamily: "'Noto Sans JP', sans-serif" }}>
      {/* Header */}
      <header className="flex items-center justify-between px-4 md:px-8 py-4">
        <Logo size="md" />
        <div className="flex items-center gap-3">
          {user ? (
            <>
              <button onClick={() => navigate('/dashboard')} className="text-sm text-white/70 hover:text-white">ダッシュボード</button>
              <button onClick={() => logout()} className="text-sm text-white/50 hover:text-white">ログアウト</button>
            </>
          ) : (
            <button onClick={() => setShowAuth(true)} className="text-sm text-white/70 hover:text-white">ログイン</button>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-4 md:px-8 pt-12 md:pt-20 pb-16 text-center">
        <div className="inline-block mb-5 px-3 py-1 rounded-full text-xs font-bold"
          style={{ background: '#34D39918', color: '#34D399', border: '1px solid #34D39944' }}>
          共通テスト対策・化学
        </div>
        <h1 className="text-3xl md:text-5xl font-black text-white leading-tight mb-4">
          化学を、<span style={{ background: 'linear-gradient(135deg,#34D399,#14B8A6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>組み立てて</span>覚える。
        </h1>
        <p className="text-white/60 text-sm md:text-lg mb-8 leading-relaxed">
          原子と結合をドラッグ＆ドロップ。構造式を自分の手で組み立てて、その場で採点。<br className="hidden md:block" />
          読むだけでは身につかない「分子のつながり方」が、手を動かすことで頭に残ります。
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button onClick={() => navigate('/questions')}
            className="px-8 py-3.5 rounded-xl font-bold text-white text-base"
            style={{ background: 'linear-gradient(135deg, #34D399, #14B8A6)' }}>
            無料で問題を解いてみる
          </button>
          <button onClick={() => (user ? navigate('/dashboard') : setShowAuth(true))}
            className="px-8 py-3.5 rounded-xl font-bold text-white/80 text-base"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}>
            {user ? 'ダッシュボードへ' : 'ログイン / 登録'}
          </button>
        </div>
        <p className="text-white/30 text-xs mt-4">Q1〜3は登録不要でお試しできます</p>
      </section>

      {/* Features */}
      <section className="max-w-5xl mx-auto px-4 md:px-8 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {FEATURES.map((f) => (
            <div key={f.title} className="p-6 rounded-2xl"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <div className="text-4xl mb-3">{f.icon}</div>
              <h3 className="text-lg font-bold text-white mb-2">{f.title}</h3>
              <p className="text-sm text-white/55 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Sample list */}
      <section className="max-w-5xl mx-auto px-4 md:px-8 pb-20 text-center">
        <h2 className="text-xl md:text-2xl font-black text-white mb-6">収録している分子（一部）</h2>
        <div className="flex flex-wrap justify-center gap-2.5">
          {SAMPLE.map((s) => (
            <span key={s} className="px-4 py-2 rounded-full text-sm font-semibold text-white/80"
              style={{ background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.3)' }}>
              {s}
            </span>
          ))}
        </div>
        <div className="mt-10">
          <button onClick={() => navigate('/questions')}
            className="px-8 py-3.5 rounded-xl font-bold text-white text-base"
            style={{ background: 'linear-gradient(135deg, #34D399, #14B8A6)' }}>
            問題一覧を見る
          </button>
        </div>
      </section>

      <footer className="border-t border-white/10 px-4 md:px-8 py-6 text-center">
        <p className="text-white/30 text-xs">
          <button onClick={() => navigate('/legal')} className="hover:text-white/60">利用規約・プライバシーポリシー</button>
          <span className="mx-2">·</span>
          ChemCraft — craft シリーズ
        </p>
      </footer>

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} onSuccess={() => { setShowAuth(false); navigate('/dashboard') }} />}
    </div>
  )
}
